/**
 * 项目缓存配置文件
 * 纯配置数据，不包含任何方法
 */

import type { CacheTimeConfig } from './cache'

// ==================== 缓存时间配置 ====================

/**
 * 缓存时间配置（毫秒）
 * 简单的时间预设
 */
export const cacheTimeConfig: CacheTimeConfig = {
    // 基础时间单位
    MINUTE: 60 * 1000,                      // 1分钟
    HOUR: 60 * 60 * 1000,                   // 1小时  
    DAY: 24 * 60 * 60 * 1000,               // 1天
    WEEK: 7 * 24 * 60 * 60 * 1000,          // 1周

    // 常用缓存时间
    SHORT: 30 * 1000,                       // 30秒 - 实时数据
    MEDIUM: 5 * 60 * 1000,                  // 5分钟 - 一般数据
    LONG: 60 * 60 * 1000,                   // 1小时 - 配置数据
    VERY_LONG: 24 * 60 * 60 * 1000,         // 1天 - 用户数据

    // 特殊用途
    DEBOUNCE: 1000,                         // 1秒 - 防重复点击
}

// ==================== 业务模块缓存配置 ====================

/**
 * 业务模块专用缓存时间配置
 * 如果某个模块没有配置，则使用代码中的默认值
 */
export const modulesCacheConfig = {
    // 配置数据模块
    ranks: 5 * 60 * 1000,      // 段位数据缓存5分钟
    skins: 5 * 60 * 1000,      // 皮肤数据缓存5分钟
    gunSkins: 5 * 60 * 1000,   // 枪皮数据缓存5分钟
    operatorSkins: 5 * 60 * 1000,   // 干员皮数据缓存5分钟
    services: 5 * 60 * 1000,   // 服务数据缓存5分钟

    // 通知模块
    notifications: 30 * 1000,  // 通知数据缓存30秒（实时性要求高）

    // 用户数据模块
    // userProfile: 10 * 60 * 1000,  // 用户资料缓存10分钟

    // 账号数据模块
    // accountList: false,            // 账号列表不缓存

    // 订单数据模块  
    // orderList: false,              // 订单列表不缓存
} as const

// ==================== 缓存键配置 ====================

/**
 * 缓存键配置
 * 使用简单的对象结构
 */
export const cacheKeys = {
    // 用户相关
    user: {
        profile: 'user:profile',
        settings: 'user:settings',
    },

    // 认证相关
    auth: {
        token: 'auth:token',
        user: 'auth:user',
        login: 'auth:login',
        sendCode: 'auth:send-code',
        resetPassword: 'auth:reset-password',
    },

    // 配置数据
    config: {
        ranks: 'config:ranks',
        // Versioned keys invalidate browsers that still have the old 20-item response cached.
        skins: 'config:skins:v2',
        gunSkins: 'config:gunSkins:v2',
        operatorSkins: 'config:operatorSkins:v2',
        services: 'config:services',
    },

    // 通知
    notification: {
        count: 'notification:count',
    },

    // 管理员
    admin: {
        stats: 'admin:stats',
        dashboard: 'admin:dashboard',
    },

    // 账号数据
    accounts: {
        list: 'accounts:list',
        detail: 'accounts:detail',
        search: 'accounts:search',
    }
} as const

// ==================== 缓存规则配置 ====================

/**
 * 缓存规则 - 简单的键值对
 * key: 缓存键或键前缀
 * value: 缓存时间（毫秒）或 false（不缓存）或 'debounce'（只防重复）
 */
export const cacheRules = {
    // ===== 需要缓存的数据 =====
    'user:profile': cacheTimeConfig.VERY_LONG,     // 用户信息缓存1天
    'auth:token': cacheTimeConfig.VERY_LONG,       // 登录令牌缓存1天
    'auth:user': cacheTimeConfig.VERY_LONG,        // 当前用户信息缓存1天
    'config:': cacheTimeConfig.LONG,               // 所有配置数据缓存1小时
    'notification:count': cacheTimeConfig.SHORT,   // 通知数量缓存30秒
    'admin:stats': cacheTimeConfig.LONG,           // 管理员统计缓存1小时

    // ===== 不缓存的数据 =====
    'accounts:list': false,                        // 账号列表不缓存
    'accounts:search:': cacheTimeConfig.SHORT,    // 账号搜索缓存30秒（避免重复搜索）
    'orders:list': false,                          // 订单列表不缓存
    'accounts:detail': false,                      // 账号详情不缓存
    'orders:detail': false,                        // 订单详情不缓存
    'notifications:list': false,                   // 通知列表不缓存
    'admin:users': false,                          // 用户管理不缓存
    'admin:orders': false,                         // 订单管理不缓存

    // ===== 只防重复点击 =====
    'accounts:create': 'debounce',                 // 创建账号防重复
    'accounts:update': 'debounce',                 // 更新账号防重复
    'orders:create': 'debounce',                   // 创建订单防重复
    'payments:': 'debounce',                       // 所有支付操作防重复
    'auth:login': false,                           // 登录操作不缓存，每次都重新请求
    'auth:send-code': 'debounce',                  // 发送验证码防重复
    'auth:reset-password': 'debounce',             // 重置密码防重复
    'ranks:create': 'debounce',                    // 创建段位防重复
    'ranks:update': 'debounce',                    // 更新段位防重复
    'ranks:delete': 'debounce',                    // 删除段位防重复
    'skins:create': 'debounce',                    // 创建皮肤防重复
    'skins:update': 'debounce',                    // 更新皮肤防重复
    'skins:delete': 'debounce',                    // 删除皮肤防重复
} as const

// ==================== 持久化配置 ====================

/**
 * 需要持久化到localStorage的缓存键
 */
export const persistKeys = [
    cacheKeys.auth.token,
    cacheKeys.auth.user,
    cacheKeys.user.profile,
    cacheKeys.config.ranks,    // 段位数据缓存5分钟，需要持久化
    cacheKeys.config.skins,    // 皮肤数据缓存5分钟，需要持久化
    cacheKeys.config.gunSkins, // 枪皮数据缓存5分钟，需要持久化
    cacheKeys.config.services, // 服务数据缓存5分钟，需要持久化
    'countdown:',              // 持久化所有倒计时键（使用前缀匹配）
]

/**
 * 缓存初始化配置
 */
export const cacheInitConfig = {
    storageKey: 'game_rental_cache_v3',
    fallbackStorageKeys: ['game_rental_cache_v2', 'app_cache'],
    persistKeys,
    cleanupInterval: 5 * 60 * 1000, // 5分钟清理一次
}

// ==================== 导出类型 ====================

export type CacheRuleType = typeof cacheRules
export type CacheKeyType = typeof cacheKeys
export type ModulesCacheConfigType = typeof modulesCacheConfig 
